import { useEffect, useMemo, useState, type RefObject } from 'react'
import { useInView } from 'framer-motion'
import AutoPlayRiveComponent from '../../../AutoPlayRiveComponent'
import { EliteLandingPageState, type EliteConsentTextItem, type ElitePlanOfferType } from '../../../../../types/misc.types'
import { eliteLandingPageStateConfig } from '../../../../../constants'
import { EliteButtonNW } from '../../../EliteButtonNW'
import { EliteButtonV3 } from '../../../EliteButtonV3'
import RenderHTML from '../../../../ui-components/RenderHTML'
import { getTopPadding } from '../../../../../utils/sessionStorage'
import { EliteOfferBanner } from './EliteOfferBanner'
import type { EliteReferralReward } from '../../../../../types/misc.types'
import { shouldShowEliteOfferBanner } from '../../../EliteSpecialOfferGiftBanner'

const StarIcon = () =>
  <RenderHTML
    innerHTMLText={`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M2.0124 6.62258L5.39212 5.39359L6.62111 2.01387C6.74928 1.66139 7.24779 1.66139 7.37596 2.01387L8.60495 5.39359L11.9846 6.62258C12.3372 6.75075 12.3372 7.24925 11.9846 7.37743L8.60495 8.60641L7.37596 11.9861C7.24779 12.3386 6.74928 12.3386 6.62111 11.9861L5.39212 8.60641L2.01241 7.37743C1.65993 7.24925 1.65993 6.75075 2.0124 6.62258Z" fill="#FFF2DF" fill-opacity="0.8"/>
</svg>`} />

export const BrokeEliteLandingPageHead = ({
  handleEliteButtonVisibility,
  isWhitelisted = 'false',
  isNotified = false,
  handleIsNotified = () => { },
  onBGRiveLoad,
  eliteLandingPageState,
  amount,
  cta,
  isForceUpdateRequired,
  version,
  isEliteButtonDisabled,
  isEliteButtonVisible,
  consentText,
  consentTextList,
  familyMemberName,
  onConsentStateChange,
  specialOffering,
  offerStartTime,
  offerExpiryTime,
  referralReward,
  offerType,
  signupOffer,
  inlineEliteCtaRef,
}: {
  handleEliteButtonVisibility: (x: boolean) => void
  isWhitelisted?: string
  isNotified?: boolean
  handleIsNotified?: () => void
  onBGRiveLoad: () => void
  eliteLandingPageState: `${EliteLandingPageState}`
  amount: number
  cta: Record<any, any> | null
  isForceUpdateRequired: boolean
  version: 'V1' | 'V2' | 'ELITE_LANDING_PAGE_1L'
  isEliteButtonDisabled?: boolean
  isEliteButtonVisible?: boolean
  consentText?: string
  consentTextList?: EliteConsentTextItem[]
  familyMemberName?: string
  onConsentStateChange?: (checked: boolean) => void
  activationExpiryDate?: string | number | undefined
  specialOffering?: boolean
  offerStartTime?: number | null
  offerExpiryTime?: number | null
  referralReward?: EliteReferralReward | null
  offerType?: ElitePlanOfferType | null
  signupOffer?: boolean | null
  /** Inline buy CTA — shared with page FAB bottom-band observer + sticky visibility. */
  inlineEliteCtaRef: RefObject<HTMLDivElement>
}) => {
  const isEliteButtonInView = useInView(inlineEliteCtaRef)

  const riveRatio = 1.74
  const [outsideRiveWidth, setOutsideRiveWidth] = useState(0)

  useEffect(() => {
    handleEliteButtonVisibility(!isEliteButtonInView)
  }, [isEliteButtonInView])

  const bgRiveheight = useMemo(() => {
    return outsideRiveWidth * (riveRatio ?? 1) || 700
  }, [outsideRiveWidth])

  const shouldShowOfferBanner = useMemo(
    () =>
      shouldShowEliteOfferBanner({
        offerType,
        specialOffering,
        signupOffer,
        offerStartTime,
        offerExpiryTime
      }),
    [offerType, specialOffering, signupOffer, offerStartTime, offerExpiryTime]
  )
  return (
    <div className='flex flex-col bg-[#010101] relative z-0 pb-10 -mt-16'>
      <div
        ref={(ref) => {
          if (ref && outsideRiveWidth === 0) {
            setOutsideRiveWidth(ref?.clientWidth ?? 300)
          }
        }}
        style={{ height: bgRiveheight }}
        className='absolute w-full'
      >
        <AutoPlayRiveComponent riveUrl='/elite_bg.riv' riveFit={'fitWidth'} isLoop={true} onRiveLoad={onBGRiveLoad} />
      </div>
      <div className='z-10 flex flex-col justify-center items-center w-full pt-[64px]'>
        <div
          style={{ paddingTop: `calc(${(outsideRiveWidth > 400 ? bgRiveheight * 0.1 : 56) + getTopPadding()}px)` }}
          className='h-auto w-full flex flex-col items-center justify-center'
        >
          <div className='h-20 w-20 ml-[6px]'>
            <img src={'/images/elite/elite_badge.png'} alt='' aria-hidden='true' className='h-full w-auto' />
          </div>
          <h2 className='w-[150px] min-h-[31px] h-max pt-2 m-0'>
            <img src={'/images/elite/elite_and_name.svg'} alt='Powerup Elite Logo' />
          </h2>
        </div>
        <h1 className='text-[#FFF2DF] font-[500] text-[30px] text-center px-4 leading-[120%] pt-10'>
          Everything you need to invest with confidence
        </h1>
        <div className='flex flex-col gap-2 opacity-80 mt-8'>
          <div className='flex items-center gap-2'>
            <StarIcon />
            <span className='text-[#FFF2DF] font-[500] text-[14px] leading-[160%]'>
              Get expert guidance for new investments
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <StarIcon />
            <span className='text-[#FFF2DF] font-[500] text-[14px] leading-[160%]'>
              Portfolio reviews by SEBI registered advisors
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <StarIcon />
            <span className='text-[#FFF2DF] font-[500] text-[14px] leading-[160%]'>
              Build a portfolio aligned to your risk and goals
            </span>
          </div>
        </div>
        {shouldShowOfferBanner && (
          <EliteOfferBanner
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            membershipFees={amount}
            offerType={offerType}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            notElitePage={false}
            cta={cta}
            source='BrokeEliteLandingPage'
            screenType='MF_Elite_Landing'
          />
        )}
        <div ref={inlineEliteCtaRef} className='pt-10 pb-2 w-full px-5'>
          <EliteButtonV3
            specialOffering={specialOffering}
            amount={amount}
            eliteLandingPageState={eliteLandingPageState}
            cta={cta}
            isForceUpdateRequired={isForceUpdateRequired}
            isButtonDisabled={isEliteButtonDisabled}
            version={version}
            isEliteButtonVisible={isEliteButtonVisible ?? false}
            consentText={consentText}
            consentTextList={consentTextList}
            familyMemberName={familyMemberName}
            onConsentStateChange={onConsentStateChange}
            showConsentText={!isEliteButtonVisible}
            referralReward={referralReward}
          />
        </div>
        {eliteLandingPageStateConfig?.[eliteLandingPageState]?.supportText ? (
          <p className='text-[#FFCC80] text-center px-5 text-[12px] font-[400] py-2 w-11/12 m-0'>
            {eliteLandingPageStateConfig?.[eliteLandingPageState]?.supportText}
          </p>
        ) : (
          <div className='py-1'></div>
        )}
      </div>
    </div>
  )
}
